import React from 'react';
import { Helmet } from 'react-helmet';

interface SEOProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  twitterCard?: 'summary' | 'summary_large_image';
  children?: React.ReactNode;
}

/**
 * Componente SEO para optimizar el SEO de las páginas
 * Incluye metadatos, Open Graph, Twitter Cards y Schema.org
 */
export default function SEO({
  title,
  description,
  canonicalUrl,
  ogImage = '/images/og-image.jpg',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  children
}: SEOProps) {
  // Asegurarse de que el título no exceda los 60 caracteres
  const formattedTitle = title.length > 60
    ? `${title.substring(0, 57)}...`
    : title;

  // Asegurarse de que la descripción no exceda los 160 caracteres
  const formattedDescription = description.length > 160
    ? `${description.substring(0, 157)}...`
    : description;

  // URL canónica por defecto
  const siteUrl = 'https://www.innotecfood.eu';
  const canonical = canonicalUrl ? `${siteUrl}${canonicalUrl}` : siteUrl;

  return (
    <Helmet>
      {/* Metadatos básicos */}
      <title>{formattedTitle}</title>
      <meta name="description" content={formattedDescription} />
      <link rel="canonical" href={canonical} />

      {/* Favicon */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="manifest" href="/site.webmanifest" />
      <meta name="theme-color" content="#FCAA0D" />

      {/* Open Graph */}
      <meta property="og:title" content={formattedTitle} />
      <meta property="og:description" content={formattedDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={`${siteUrl}${ogImage}`} />
      <meta property="og:site_name" content="Innotec Food Equipment" />

      {/* Twitter Cards */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={formattedTitle} />
      <meta name="twitter:description" content={formattedDescription} />
      <meta name="twitter:image" content={`${siteUrl}${ogImage}`} />

      {/* Metadatos adicionales */}
      <meta name="robots" content="index, follow" />
      <meta name="language" content="Spanish" />
      <meta name="author" content="Innotec Food Equipment" />

      {/* Contenido adicional proporcionado por el componente padre */}
      {children}
    </Helmet>
  );
}
